import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const blank = { name: "", email: "", phone: "", skill_level: "Beginner", goals: "", notes: "" };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);

  const load = async () => {
    const { data } = await api.get("/clients");
    setClients(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/clients", form);
      toast.success("Client added");
      setForm(blank);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    await api.delete(`/clients/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="p-8 md:p-12" data-testid="clients-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-2">Roster</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight">Clients.</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm" data-testid="add-client-btn">
              <Plus className="h-4 w-4 mr-2" /> New client
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0F0F0F] border-[#27272A] text-white max-w-lg" data-testid="add-client-dialog">
            <DialogHeader>
              <DialogTitle className="font-display uppercase">Add client</DialogTitle>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Name</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="client-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Email</Label>
                  <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="client-email-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="client-phone-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Skill Level</Label>
                  <Select value={form.skill_level} onValueChange={(v) => setForm({ ...form, skill_level: v })}>
                    <SelectTrigger className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="client-skill-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0F0F0F] border-[#27272A] text-white">
                      <SelectItem value="Beginner">Beginner</SelectItem>
                      <SelectItem value="Intermediate">Intermediate</SelectItem>
                      <SelectItem value="Advanced">Advanced</SelectItem>
                      <SelectItem value="Competitor">Competitor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Goals</Label>
                <Textarea value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} rows={2} className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="client-goals-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="client-notes-input" />
              </div>
              <Button type="submit" className="w-full bg-[#FF331F] hover:bg-[#FF5242] font-bold uppercase tracking-widest rounded-sm" data-testid="save-client-btn">Save Client</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-[#27272A] bg-[#0F0F0F]">
        <table className="w-full text-sm" data-testid="clients-table">
          <thead className="border-b border-[#27272A] text-zinc-500 uppercase tracking-widest text-[10px]">
            <tr>
              <th className="text-left p-4">Name</th>
              <th className="text-left p-4">Email</th>
              <th className="text-left p-4">Phone</th>
              <th className="text-left p-4">Skill</th>
              <th className="p-4 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 ? (
              <tr><td colSpan={5} className="p-12 text-center text-zinc-500">No clients yet. Add your first.</td></tr>
            ) : clients.map((c) => (
              <tr key={c.id} className="border-b border-[#27272A] hover:bg-[#121212]" data-testid={`client-row-${c.id}`}>
                <td className="p-4 font-display uppercase">{c.name}</td>
                <td className="p-4 text-zinc-400">{c.email}</td>
                <td className="p-4 text-zinc-400">{c.phone || "—"}</td>
                <td className="p-4"><span className="text-[10px] uppercase tracking-widest px-2 py-1 bg-[#FF331F]/10 text-[#FF331F]">{c.skill_level}</span></td>
                <td className="p-4 text-right">
                  <button onClick={() => remove(c.id)} className="text-zinc-500 hover:text-red-400" data-testid={`delete-client-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
